import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './ArticlePage.css';
import Navbar from './Navbar';
import Footer from './Footer';

// Social sharing images (with optimized dimensions for different platforms)
const socialImages = {
  'ai-cdfis-part-2': '/images/social/ai-cdfis-part2-social.jpg',
  'six-ways-amplify-mission': '/images/social/six-ways-amplify-mission-social.jpg',
  // Add more social images for other articles as needed
};

// Default social image if article-specific one isn't available
const defaultSocialImage = '/images/social/clarity-social-default.jpg';

const articles = {
  'ai-cdfis-part-2': {
    title: "AI and CDFIs: From Concept to Implementation (Part II)",
    author: "Dr. Maria Rodriguez",
    date: "March 23, 2025",
    category: "Technology & Innovation",
    imageUrl: "/images/cdfi-community-school.jpg",
    socialImageUrl: '/images/social/ai-cdfis-part2-social.jpg',
    linkedinUrl: "https://www.linkedin.com/pulse/ai-cdfis-from-concept-implementation-part-ii-amir-ali-ncxbe/",
    excerpt: 'A year ago, we explored how CDFIs could leverage Artificial Intelligence. Since then, AI technology has advanced rapidly.',
    redirectToLinkedIn: true
  },
  'six-ways-amplify-mission': {
    title: "Six Ways to Amplify Your Mission: A CDFI's AI Implementation Guide",
    author: "Amir A.",
    date: "July 23, 2025",
    category: "AI & Technology",
    imageUrl: "/images/ai-cdfi-implementation.jpg",
    socialImageUrl: '/images/social/six-ways-amplify-mission-social.jpg',
    linkedinUrl: "https://www.linkedin.com/pulse/six-ways-amplify-your-mission-cdfis-ai-implementation-amir-ali-cfa-dbzwe/?trackingId=m59%2BPTg8SFSepbXulCnfhg%3D%3D",
    excerpt: 'Let\'s be honest, when we in the impact finance world hear "Artificial Intelligence," we don\'t immediately think of the words trust and support. Instead, we think of ChatGPT that can plan a vacation or write a silly poem—tools that feel far away from the serious work of building relationships and getting projects and businesses funded.',
    redirectToLinkedIn: false,
    content: `
      <p>Let's be honest, when we in the impact finance world hear 'Artificial Intelligence,' we don't immediately think of the words trust and support. Instead, we think of ChatGPT that can plan a vacation or write a silly poem—tools that feel far away from the serious work of building relationships and getting projects and businesses funded. After spending the last two years testing these tools on the ground, I've seen firsthand that when you move beyond the surface and use them with real intention (i.e. learn how to prompt, build policies around the use of AI in your organization), AI becomes a powerful force for amplifying—not replacing—our work.</p>

      <p>In this post, I'll walk through six of those real-world examples, from beginner steps to advanced strategies. More importantly, I'll share the critical guardrails and responsible frameworks I have developed to ensure these powerful tools serve their mission, not detract from it.</p>

      <p>The framework I'm sharing is the culmination of two years of intensive research and is currently being discussed with institutions looking to become pioneers in this space. While the journey is just beginning, the destination is clear: a sector where our teams are freed up for deeper borrower support and our organizations can make smarter, faster, more mission-aligned decisions.</p>

      <h2>The Pioneer's Checklist: An AI Readiness Assessment</h2>

      <p>Becoming a pioneer requires a solid basecamp. Before you start the journey, here's a quick checklist to see if your organization is ready to lead:</p>

      <ol>
        <li><strong>Team Capacity:</strong> Do you have at least one tech-comfortable staff member—a potential "AI Champion"—who can dedicate 2-3 hours per week to learning and experimenting with these tools?</li>
        <li><strong>Financial Commitment:</strong> Can you commit a realistic budget for responsible AI implementation? Tool subscriptions ($50-200/month) are just the start. Factor in hidden costs:
          <ul>
            <li>Staff time for training and monitoring: $800-1,500/month in opportunity cost</li>
            <li>Legal review and policy development: $5,000-10,000 one-time setup</li>
            <li>Realistic first-year budget: $15,000-25,000 for small CDFIs</li>
          </ul>
        </li>
        <li><strong>Data Governance:</strong> Do you have a basic policy, even an informal one, for how you handle and protect sensitive borrower or organizational information?</li>
        <li><strong>Cultural Mindset:</strong> Is your team willing to start small and learn as you go, rather than seeking a perfect, all-in-one solution from day one?</li>
        <li><strong>Leadership Buy-In:</strong> Does your leadership support the idea of experimenting with new tools—including the possibility of small failures and the need to iterate?</li>
      </ol>

      <p>If you answered yes to three or more of these questions, your organization is ready to start. If not, the best first step is to focus on building these foundational elements before diving into new technology.</p>

      <h2>The Responsible AI Framework: Your Compass</h2>

      <p>Once you've confirmed your readiness, the next step isn't to jump into tools—it's to build your fence. A strong, responsible framework is the compass that ensures every tool you adopt serves your mission. Here are the five pillars that have kept my strategic planning compliant, effective, and mission-aligned.</p>

      <h3>1. Beyond Efficiency: A Commitment to Equity</h3>

      <p>The greatest risk of AI is not a server crash; it's "equity-washing"—using technology to do biased work faster. To prevent this, you must proactively audit for bias from day one.</p>

      <p><strong>The Equity Question Checklist:</strong> For every major AI output, ask these questions:</p>

      <ul>
        <li>Does this analysis reflect our community's actual, lived needs, or does it reflect historical banking patterns that we aim to disrupt?</li>
        <li>Would a community member recognize their own neighborhood in this data?</li>
        <li>Are we using this tool to challenge our own assumptions, or merely to confirm them faster?</li>
      </ul>

      <h3>2. Avoiding the Vendor Trap</h3>

      <p>As a CDFI leader, arm yourself with this vendor evaluation checklist.</p>

      <p><strong>Red Flags to Run From:</strong></p>
      <ul>
        <li>Vendors who can't explain their AI model in simple terms.</li>
        <li>"Black box" solutions where you can't see how decisions are derived.</li>
        <li>A lack of clear, specific data privacy and security protocols.</li>
      </ul>

      <p><strong>Questions That Separate Good Vendors from Bad Ones:</strong></p>
      <ul>
        <li>"Can you show me exactly what kind of data your model was trained on, and how you tested it for bias?"</li>
        <li>"What happens to our organization's data? Where is it stored, who has access, and what are your data destruction policies?"</li>
      </ul>

      <h3>3. Regulatory-Ready AI Governance</h3>

      <p>Your AI governance must address the specific regulatory environment CDFIs operate within. You need frameworks that will satisfy auditors and examiners from multiple agencies.</p>

      <p><strong>Core Regulatory Requirements:</strong></p>
      <ul>
        <li><strong>Model Risk Management:</strong> Document how AI tools are validated, monitored, and back-tested. Even "simple" AI applications like meeting transcription tools need governance if they influence business decisions.</li>
        <li><strong>Fair Lending Compliance:</strong> Establish testing protocols to ensure AI doesn't create disparate impact. This includes regular statistical analysis of loan outcomes by protected class.</li>
        <li><strong>Vendor Management:</strong> Create enhanced due diligence for AI vendors that covers data security, algorithm transparency, business continuity, and regulatory examination support.</li>
      </ul>

      <p><strong>Documentation Requirements:</strong></p>
      <ul>
        <li>Decision audit trails for any AI-assisted lending decisions</li>
        <li>Bias testing results and remediation plans</li>
        <li>Vendor risk assessments updated annually</li>
        <li>Staff training records on AI governance and fair lending</li>
      </ul>

      <p><strong>The Explainability Standard:</strong> This is the golden rule. For any AI-assisted decision, a staff member must be able to explain the logic to a borrower, your board, and an auditor in simple terms. If you can't explain it, you can't use it.</p>

      <h3>4. Preserving the Human Touch</h3>

      <p>AI should prepare your team for better conversations, not replace them. The rule is simple: AI handles the data; humans handle the relationship. This means an AI can summarize financial statements to identify a revenue dip, but only a loan officer can sit across from a borrower and ask, "I see things were tough last spring; was everything okay?" That question is where trust is built, and it can't be automated.</p>

      <h3>5. Community Engagement Framework</h3>

      <p>AI decisions affect your community directly. They deserve a voice in how these tools are designed and deployed. Focus community engagement on high-impact decisions and values alignment, not technical implementation details.</p>

      <p><strong>Three-Tier Engagement Model:</strong></p>
      <ul>
        <li><strong>Tier 1 - Community Input Required:</strong> AI applications that directly affect borrower interactions (loan decisioning tools, customer-facing chatbots, automated communications)</li>
        <li><strong>Tier 2 - Community Notification:</strong> Significant changes to existing AI tools or new applications that handle borrower data</li>
        <li><strong>Tier 3 - Internal Operations:</strong> Back-office efficiency tools (meeting transcription, internal data analysis) that don't directly touch community interactions</li>
      </ul>

      <h2>Red Flags: When to Pump the Brakes</h2>

      <p>Being a pioneer also means knowing when to pause and re-evaluate. If you notice any of the following, it's a sign to slow down and reassess your approach before moving forward.</p>

      <ul>
        <li><strong>Over-Reliance:</strong> Staff are starting to accept AI recommendations without critical review or are losing confidence in their own professional judgment.</li>
        <li><strong>Mission Drift:</strong> Your team is spending more time talking about the efficiency of the tool than about the quality of their interactions with borrowers and the community.</li>
        <li><strong>Community Disconnect:</strong> The AI's outputs (like a market analysis) feel sterile and don't reflect the nuanced, on-the-ground knowledge your team has about your community.</li>
        <li><strong>Speed Over Substance:</strong> There's a new pressure to make decisions faster rather than using the time saved to make decisions wiser.</li>
        <li><strong>Budget Strain:</strong> The costs of tool subscriptions are starting to outweigh the perceived value or time saved.</li>
      </ul>

      <h2>A Call to Action: Your Turn on the Frontier</h2>

      <p>The journey into artificial intelligence can feel daunting, especially for a sector built on human trust and deep relationships. It's easy to look at these powerful new tools and see only risk or complexity.</p>

      <p>But as this guide has shown, the path forward doesn't require a massive tech budget or a team of data scientists. It requires the same methodical, mission-centered approach that has always been the strength of community finance. It starts with a simple readiness check, a clear governance policy, and a single, low-cost tool for a well-defined problem. It's about building a framework where your wisdom guides the technology, not the other way around.</p>

      <p>The CDFIs who succeed with AI won't be the ones with the most advanced algorithms. They will be the ones who remember that behind every data point is a person, and behind every efficiency gained is a new opportunity for a deeper human connection.</p>

      <p>The question is no longer if AI will transform our work, but how we will lead that transformation. The frontier is open. This guide is your map. It's time to take the first step.</p>
    `
  }
  // More articles would be defined here
};

const ArticlePage = () => {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const getArticle = () => {
      const foundArticle = articles[articleId];
      setArticle(foundArticle);
      
      // Redirect to LinkedIn article if specified
      if (foundArticle && foundArticle.redirectToLinkedIn && foundArticle.linkedinUrl) {
        window.location.href = foundArticle.linkedinUrl;
      }
    };

    getArticle();
  }, [articleId]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (!article) {
    return (
      <div className="article-page loading">
        <Navbar />
        <div className="container">
          <h1>Loading article...</h1>
        </div>
      </div>
    );
  }

  // Get the social sharing image URL (use article-specific if available, otherwise default)
  const socialImageUrl = article.socialImageUrl 
    ? `${window.location.origin}${article.socialImageUrl}`
    : `${window.location.origin}${defaultSocialImage}`;

  // If article should redirect to LinkedIn, show redirect message
  if (article.redirectToLinkedIn && article.linkedinUrl) {
    return (
      <div className="article-page">
        <Helmet>
          <title>{article.title} | Clarity Impact Finance</title>
          <meta name="description" content={article.excerpt} />
          
          {/* OpenGraph Meta Tags for social sharing */}
          <meta property="og:title" content={article.title} />
          <meta property="og:description" content={article.excerpt} />
          <meta property="og:image" content={socialImageUrl} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:url" content={window.location.href} />
          <meta property="og:type" content="article" />
          
          {/* Twitter Card meta tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={article.title} />
          <meta name="twitter:description" content={article.excerpt} />
          <meta name="twitter:image" content={socialImageUrl} />
          
          {/* LinkedIn specific */}
          <meta property="og:site_name" content="Clarity Impact Finance" />
          <meta property="article:published_time" content={new Date(article.date).toISOString()} />
          <meta property="article:author" content={article.author} />
        </Helmet>
        
        <div className="redirect-message">
          <div className="redirect-container">
            <i className="fab fa-linkedin"></i>
            <h2>Redirecting to LinkedIn article...</h2>
            <p>If you are not automatically redirected, <a href={article.linkedinUrl}>click here</a>.</p>
          </div>
        </div>
      </div>
    );
  }

  // Render the full article content
  return (
    <div className="article-page">
      <Helmet>
        <title>{article.title} | Clarity Impact Finance</title>
        <meta name="description" content={article.excerpt} />
        
        {/* OpenGraph Meta Tags for social sharing */}
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:image" content={socialImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="article" />
        
        {/* Twitter Card meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt} />
        <meta name="twitter:image" content={socialImageUrl} />
        
        {/* LinkedIn specific */}
        <meta property="og:site_name" content="Clarity Impact Finance" />
        <meta property="article:published_time" content={new Date(article.date).toISOString()} />
        <meta property="article:author" content={article.author} />
      </Helmet>
      
      <Navbar />
      
      <div className="article-container">
        <div className="article-header">
          <div className="container">
            <div className="article-meta">
              <span className="article-category">{article.category}</span>
              <span className="article-date">{article.date}</span>
            </div>
            <h1 className="article-title">{article.title}</h1>
            <p className="article-excerpt">{article.excerpt}</p>
            <div className="article-author">
              <span>By {article.author}</span>
              {article.linkedinUrl && (
                <a href={article.linkedinUrl} target="_blank" rel="noopener noreferrer" className="linkedin-link">
                  <i className="fab fa-linkedin"></i>
                  Read on LinkedIn
                </a>
              )}
            </div>
          </div>
        </div>
        
        <div className="article-content">
          <div className="container">
            <div className="content-wrapper" dangerouslySetInnerHTML={{ __html: article.content }} />
          </div>
        </div>
        
        <div className="article-actions">
          <div className="container">
            <Link to="/blog" className="back-to-blog">
              <i className="fas fa-arrow-left"></i>
              Back to Blog
            </Link>
            {article.linkedinUrl && (
              <a href={article.linkedinUrl} target="_blank" rel="noopener noreferrer" className="share-on-linkedin">
                <i className="fab fa-linkedin"></i>
                Share on LinkedIn
              </a>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ArticlePage;